import type { ExpValue } from '@/domain/types'

export const EXP_REQUIREMENT_SAMPLE_COUNT = 3
export const EXP_REQUIREMENT_RELATIVE_TOLERANCE = 0.005
export const EXP_RATIO_MATCH_TOLERANCE = 0.0002

export interface ExpRequirementRange {
  readonly minimum: number
  readonly maximum: number
}

interface RequirementSample {
  readonly absolute: number
  readonly ratio: number
  readonly range: ExpRequirementRange
  readonly sourceAt: number
}

interface LevelRequirementState {
  confirmed: number | undefined
  samples: RequirementSample[]
  zeroSamples: number[]
  zeroConfirmed: boolean
}

export interface ExpRequirementObservationOptions {
  /** Decimal places shown after the percentage separator in the game UI. */
  readonly percentagePrecision?: number
  /** The recognizer has already confirmed this value using independent evidence. */
  readonly independentlyConfirmed?: boolean
}

export interface ExpRequirementObservation {
  readonly accepted: boolean
  readonly requiredExp: number | null
  readonly approximate: boolean
  readonly confirming: boolean
}

/** Infers level requirements from runtime OCR without a bundled game data table. */
export class ExpRequirementEstimator {
  private readonly levels = new Map<number, LevelRequirementState>()

  reset(seed?: { level: number; value: ExpValue } | null): void {
    this.levels.clear()
    if (seed) this.seed(seed.level, seed.value)
  }

  seed(level: number, value: ExpValue): void {
    if (!validLevel(level) || value.estimated || value.percentageOnly) return
    const required = normalizedRequirement(value.requiredExp)
    if (required !== null) {
      this.levels.set(level, { confirmed: required, samples: [], zeroSamples: [], zeroConfirmed: false })
      return
    }
    if (value.absolute === 0 && value.ratio === 0) {
      this.levels.set(level, { confirmed: undefined, samples: [], zeroSamples: [], zeroConfirmed: true })
    }
  }

  observe(
    level: number,
    value: ExpValue,
    sourceAt: number,
    options: ExpRequirementObservationOptions = {},
  ): ExpRequirementObservation {
    if (!validLevel(level) || !validExactExp(value) || !Number.isFinite(sourceAt)) return unavailable()

    const state = this.levels.get(level) ?? { confirmed: undefined, samples: [], zeroSamples: [], zeroConfirmed: false }
    this.levels.set(level, state)

    const carriedRequirement = normalizedRequirement(value.requiredExp)
    if (carriedRequirement !== null) {
      state.confirmed = carriedRequirement
      state.samples = []
      state.zeroSamples = []
      state.zeroConfirmed = false
      return confirmed(carriedRequirement)
    }

    if (value.absolute === 0 && value.ratio === 0) {
      if (state.confirmed !== undefined) return confirmed(state.confirmed)
      if (state.zeroConfirmed) return confirmed(null)
      const previousZeroAt = state.zeroSamples.at(-1)
      if (previousZeroAt !== undefined && sourceAt <= previousZeroAt) return pending(null)
      state.zeroSamples = [...state.zeroSamples.slice(-(EXP_REQUIREMENT_SAMPLE_COUNT - 1)), sourceAt]
      if (options.independentlyConfirmed || state.zeroSamples.length >= EXP_REQUIREMENT_SAMPLE_COUNT) {
        state.zeroConfirmed = true
        state.zeroSamples = []
        return confirmed(null)
      }
      return pending(null)
    }

    const range = expRequirementRange(value, options.percentagePrecision)
    if (!range) return unavailable(state.confirmed ?? null)
    if (range.maximum === Number.MAX_SAFE_INTEGER) {
      if (state.confirmed !== undefined && requirementRangeContains(range, state.confirmed)) return confirmed(state.confirmed)
      state.confirmed = undefined
      state.samples = []
      return confirmed(null)
    }

    if (state.confirmed !== undefined && requirementRangeContains(range, state.confirmed)) {
      state.samples = []
      state.zeroSamples = []
      state.zeroConfirmed = false
      return confirmed(state.confirmed)
    }

    const requiredExp = this.observeRequirementCandidate(
      state,
      value,
      range,
      sourceAt,
      options.independentlyConfirmed ? 1 : EXP_REQUIREMENT_SAMPLE_COUNT,
    )
    state.zeroSamples = []
    state.zeroConfirmed = false
    if (requiredExp === null) return pending(state.confirmed ?? null)
    state.confirmed = requiredExp
    return confirmed(requiredExp)
  }

  requirementFor(level: number): number | null | undefined {
    const state = this.levels.get(level)
    return state?.confirmed ?? (state?.zeroConfirmed ? null : undefined)
  }

  private observeRequirementCandidate(
    state: LevelRequirementState,
    value: ExpValue & { absolute: number; ratio: number },
    range: ExpRequirementRange,
    sourceAt: number,
    requiredSamples: number,
  ): number | null {
    const sample: RequirementSample = { absolute: value.absolute, ratio: value.ratio, range, sourceAt }
    const previous = state.samples.at(-1)
    if (previous && sourceAt <= previous.sourceAt) return null
    if (!previous || samplesAreCompatible(previous, sample)) {
      state.samples = [...state.samples.slice(-(EXP_REQUIREMENT_SAMPLE_COUNT - 1)), sample]
    } else {
      state.samples = [sample]
    }
    if (state.samples.length < requiredSamples) return null

    const intersection = intersectExpRequirementRanges(state.samples.map((entry) => entry.range))
    if (!intersection) {
      state.samples = [sample]
      return null
    }
    state.samples = []
    return representativeExpRequirement(intersection, sample.absolute, inferExpRequirement(value))
  }
}

export function inferExpRequirement(value: ExpValue): number | null {
  if (!validExactExp(value) || value.ratio === 0) return null
  const inferred = value.absolute! / value.ratio
  return Number.isFinite(inferred) && inferred > 0 ? inferred : null
}

/** Models the whole-number level requirement compatible with a rounded percentage. */
export function expRequirementRange(
  value: Pick<ExpValue, 'absolute' | 'ratio' | 'percentageRounding' | 'percentagePrecision'>,
  percentagePrecision = 2,
): ExpRequirementRange | null | undefined {
  const { absolute, ratio } = value
  if (value.percentageRounding === 'floor') percentagePrecision = value.percentagePrecision ?? percentagePrecision
  if (absolute === null || ratio === null
    || !Number.isSafeInteger(absolute) || absolute < 0
    || !Number.isFinite(ratio) || ratio < 0 || ratio > 1
    || !Number.isInteger(percentagePrecision) || percentagePrecision < 0 || percentagePrecision > 4) {
    return undefined
  }
  if (absolute === 0 && ratio === 0) return null
  if (value.percentageRounding === 'floor' && absolute > 0) {
    const step = 10 ** (-(value.percentagePrecision ?? percentagePrecision)) / 100
    // The upper percentage boundary is exclusive. Zero percent supplies a
    // lower bound only; never turn that unbounded interval into an estimate.
    const lower = absolute / Math.min(1, ratio + step)
    const minimum = ratio === 1 ? absolute : Math.max(absolute, Math.floor(lower + Number.EPSILON * Math.abs(lower) * 4) + 1)
    const maximum = ratio === 0 ? Number.MAX_SAFE_INTEGER : Math.floor(absolute / ratio + Number.EPSILON * absolute / ratio * 4)
    return minimum <= maximum ? { minimum, maximum } : undefined
  }
  if (absolute <= 0 || ratio <= 0) return undefined

  const halfStep = 0.5 * 10 ** (-percentagePrecision) / 100
  const minimumRatio = Math.max(Number.EPSILON, ratio - halfStep)
  const maximumRatio = Math.min(1, ratio + halfStep)
  // Decimal HUD percentages can land exactly on an integer requirement.
  // Remove only floating-point division noise before rounding the endpoints;
  // otherwise a valid boundary can exclude the true requirement by one EXP.
  const lower = absolute / maximumRatio, upper = absolute / minimumRatio
  const minimum = Math.max(absolute, Math.ceil(lower - Number.EPSILON * Math.abs(lower) * 4))
  const maximum = Math.floor(upper + Number.EPSILON * Math.abs(upper) * 4)
  return Number.isSafeInteger(minimum) && Number.isSafeInteger(maximum) && minimum > 0 && maximum >= minimum
    ? { minimum, maximum }
    : undefined
}

export function intersectExpRequirementRanges(
  ranges: readonly ExpRequirementRange[],
): ExpRequirementRange | undefined {
  if (!ranges.length) return undefined
  const minimum = Math.max(...ranges.map((range) => range.minimum))
  const maximum = Math.min(...ranges.map((range) => range.maximum))
  return minimum <= maximum ? { minimum, maximum } : undefined
}

export function requirementRangeContains(range: ExpRequirementRange, requirement: number): boolean {
  return Number.isSafeInteger(requirement)
    && requirement >= range.minimum
    && requirement <= range.maximum
}

export function representativeExpRequirement(
  range: ExpRequirementRange,
  absolute = 0,
  preferred?: number | null,
): number {
  const roundedPreferred = preferred === null || preferred === undefined ? null : Math.round(preferred)
  if (roundedPreferred !== null && requirementRangeContains(range, roundedPreferred)) {
    return Math.max(absolute, roundedPreferred)
  }
  return Math.max(absolute, Math.round((range.minimum + range.maximum) / 2))
}

export function expMatchesRequirement(
  value: ExpValue,
  requiredExp: number | null,
  percentagePrecision = value.percentagePrecision ?? 2,
): boolean {
  if (!validExactExp(value)) return false
  if (value.absolute === 0 && value.ratio === 0) return true
  if (requiredExp === null && value.percentageRounding === 'floor' && value.ratio === 0) return expRequirementRange(value, percentagePrecision) !== undefined
  if (requiredExp === null || requiredExp <= 0) return false
  const range = expRequirementRange(value, percentagePrecision)
  return range !== null && range !== undefined && requirementRangeContains(range, requiredExp)
}

export function enrichExpRequirement(
  value: ExpValue,
  requiredExp: number | null,
  approximate = requiredExp !== null,
): ExpValue {
  return { ...value, requiredExp, requiredExpApproximate: approximate }
}

function samplesAreCompatible(first: RequirementSample, second: RequirementSample): boolean {
  return second.sourceAt > first.sourceAt
    && second.absolute >= first.absolute
    && second.ratio >= first.ratio - EXP_RATIO_MATCH_TOLERANCE
    && intersectExpRequirementRanges([first.range, second.range]) !== undefined
}

function validExactExp(value: ExpValue): value is ExpValue & { absolute: number; ratio: number } {
  return value.absolute !== null
    && value.ratio !== null
    && !value.estimated
    && Number.isSafeInteger(value.absolute)
    && value.absolute >= 0
    && Number.isFinite(value.ratio)
    && value.ratio >= 0
    && value.ratio <= 1
}

function normalizedRequirement(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : null
}

function validLevel(level: number): boolean {
  return Number.isInteger(level) && level >= 1 && level <= 200
}

function confirmed(requiredExp: number | null): ExpRequirementObservation {
  return { accepted: true, requiredExp, approximate: requiredExp !== null, confirming: false }
}

function pending(requiredExp: number | null): ExpRequirementObservation {
  return { accepted: false, requiredExp, approximate: requiredExp !== null, confirming: true }
}

function unavailable(requiredExp: number | null = null): ExpRequirementObservation {
  return { accepted: false, requiredExp, approximate: requiredExp !== null, confirming: false }
}
